import React from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '../Button/Button';
import type { LayoutContentProps } from './LayoutContent.types';

/**
 * Componente LayoutContent del sistema de diseño Siesa
 *
 * Estructura de layout que envuelve una vista completa. Incluye una cabecera
 * superior (Toolbar) pegajosa con título, opción de retorno y botón de acción,
 * junto con un área de contenido principal.
 *
 * Implementa mejores prácticas:
 * - Soporte completo para modo oscuro (dark mode)
 * - Diseño pixel-perfect basado en Figma
 * - Uso estricto de tokens de color del sistema
 *
 * @example
 * ```tsx
 * <LayoutContent 
 *   title="Entidades Dinámicas"
 *   onBack={() => history.back()}
 *   primaryAction={{
 *     label: "Nuevo",
 *     onClick: () => openModal(),
 *     icon: <PlusIcon />
 *   }}
 * >
 *   <p>Contenido principal aquí</p>
 * </LayoutContent>
 * ```
 */
export const LayoutContent: React.FC<LayoutContentProps> = ({
    title,
    onBack,
    primaryAction,
    toolbarEndContent,
    children,
    className = '',
}) => {
    // ===== CONTENEDOR PRINCIPAL =====
    const containerClasses = `
      flex
      flex-col
      w-full
      h-full
      relative
      bg-bg-primary
      dark:bg-dark-bg-primary
      ${className}
    `.trim().replace(/\s+/g, ' ');

    // ===== TOOLBAR WRAPPER =====
    const toolbarClasses = `
      sticky
      top-0
      w-full
      z-10
      flex
      items-center
      min-h-[72px]
      bg-bg-primary
      dark:bg-dark-bg-primary
    `.trim().replace(/\s+/g, ' ');

    // ===== TOOLBAR INTERIOR =====
    const toolbarInnerClasses = `
      flex-1
      flex
      items-center
      justify-between
      px-5
      min-h-[72px]
      rounded-tl-3xl
      border-b
      border-border-primary
      bg-background-secondary
      dark:bg-dark-bg-primary
      dark:border-dark-border-primary
    `.trim().replace(/\s+/g, ' ');

    // ===== ÁREA DE CONTENIDO =====
    const contentAreaClasses = `
      flex-1
      w-full
      p-6
      bg-background-secondary
      dark:bg-dark-bg-primary
    `.trim().replace(/\s+/g, ' ');

    return (
        <div className={containerClasses}>
            {/* Cabecera (Toolbar) */}
            <div className={toolbarClasses}>
                <div className={toolbarInnerClasses}>
                    <div className="flex items-center gap-2">
                        {onBack && (
                            <Button 
                                type="plain" 
                                size="base"
                                iconOnly 
                                leftIcon={<ChevronLeftIcon className="w-5 h-5 text-primary-600 dark:text-primary-600" />}
                                onClick={onBack}
                                ariaLabel="Volver a la vista anterior"
                            />
                        )}
                        <h1 className="text-[20px] font-bold text-[#1355b7] dark:text-[#3c9bf6] tracking-tight leading-[28px] m-0">
                            {title}
                        </h1>
                    </div>
                    
                    <div className="flex items-center gap-2 justify-end min-w-[256px]">
                        {toolbarEndContent ? (
                            toolbarEndContent
                        ) : primaryAction ? (
                            <Button
                                type={primaryAction.type || 'default'}
                                size={primaryAction.size || 'sm'}
                                leftIcon={primaryAction.icon}
                                onClick={primaryAction.onClick}
                                disabled={primaryAction.disabled}
                            >
                                {primaryAction.label}
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Contenido Principal */}
            <div className={contentAreaClasses}>
                {children}
            </div>
        </div>
    );
};
